import React from "react"

import { Script, Link } from "gatsby"

// Temporary rollback until we can figure out how to get the i18n links working, issue @ https://github.com/zestia/gatsby/issues/841
// import { Link } from "@zestia/gatsby-plugin-react-i18next"

import Accordion from "@/components/Accordion"
import AccordionItem from "@/components/AccordionItem"
import {
  TableOfContents,
  TableOfContentsLink,
} from "@/components/TableOfContents"
import BlogVimeoVideo from "@/components/BlogVimeoVideo"
import Video from "@/components/Video"
import FeedbackMessage from "@/components/FeedbackMessage"
import { ActionSteps, ActionStep } from "@/components/ActionSteps"
import Blockquote from "@/components/Blockquote"
import Postquote from "@/components/Postquote"
import EmailTemplate from "@/components/EmailTemplate"
import MediaWrapper from "@/components/MediaWrapper"
import QuickbooksUpsell from "@/components/QuickbooksUpsell"
import { Checklist, ChecklistItem } from "@/components/Checklist"
import Longform from "@/components/Longform"
import CallToActionBanner from "@/components/CallToActionBanner"
import isExternalLink from "@/utils/is-external-link"
import convertCapsuleLink from "@/utils/convert-capsule-link"
import SupportImage from "@/components/SupportImage"
import {
  ContentSwitcher,
  ContentSwitcherTab,
} from "@/components/ContentSwitcher"
import WistiaVideoEmbed from "@/components/WistiaVideoEmbed"
import UserInterfaceIcon from "@/components/UserInterfaceIcon"
import PlanType from "@/components/PlanType"
import Supported2faApps from "@/components/Supported2faApps"
import SupportedBrowsers from "@/components/SupportedBrowsers"
import SupportVideo from "@/components/SupportVideo"
import BlogVideo from "@/components/BlogVideo"
import TryFreeCallToAction from "@/components/TryFreeCallToAction"
import GuidedZaps from "@/components/GuidedZaps"
import IntegrationsHighlight from "@/components/IntegrationsHighlight"
import HeadingAnchor from "@/components/HeadingAnchor"
import ScrollSpy from "@/components/ScrollSpy"
import SocialShare from "@/components/SocialShare"
import CapsuleTag from "@/components/CapsuleTag"
import TableWrapper from "@/components/TableWrapper"
import { SideMenu, SideMenuLink } from "@/components/SideMenu"
import LinkableHeading from "@/components/LinkableHeading"
import InlineCTA, {
  InlineCTAGraphic,
  InlineCTAUI,
} from "@/components/InlineCTA"
import RecommendedReading from "@/components/RecommendedReading"
import LazyVideo from "@/components/LazyVideo"
import { PortableText } from "@portabletext/react"
import SanityImage from "gatsby-plugin-sanity-image"
import getSanityVideoSource from "@/utils/get-sanity-video-source"
import urlFor from "@/utils/sanity-url-builder"

const SanityContentWrapper = ({
  typeOverrides = {},
  markOverrides = {},
  children,
}) => {
  const componentsThatAllPagesCanUseWithoutImporting = {
    Link,
    Accordion,
    AccordionItem,
    TableOfContents,
    TableOfContentsLink,
    BlogVimeoVideo,
    Video,
    FeedbackMessage,
    ActionSteps,
    ActionStep,
    Blockquote,
    Postquote,
    EmailTemplate,
    MediaWrapper,
    QuickbooksUpsell,
    Checklist,
    ChecklistItem,
    Longform,
    CallToActionBanner,
    SupportImage,
    ContentSwitcher,
    ContentSwitcherTab,
    WistiaVideoEmbed,
    UserInterfaceIcon,
    PlanType,
    Supported2faApps,
    SupportedBrowsers,
    SupportVideo,
    BlogVideo,
    TryFreeCallToAction,
    GuidedZaps,
    Script,
    HeadingAnchor,
    ScrollSpy,
    SocialShare,
    SideMenu,
    SideMenuLink,
    CapsuleTag,
    TableWrapper,
    InlineCTA,
    RecommendedReading,
  }

  const components = {
    types: {
      image: ({ value }) => {
        if (value?.asset) {
          return (
            <MediaWrapper
              wide={value?.wide ? "true" : "false"}
              background={value?.background ? "true" : "false"}
            >
              {/* If animated, dont transform, else use gatsby-plugin-sanity-image */}
              {value.asset._ref && value.asset._ref.endsWith("-gif") ? (
                <img src={urlFor(value)} alt={value?.alt} />
              ) : (
                <SanityImage
                  {...value}
                  width={700}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              )}
            </MediaWrapper>
          )
        }
      },
      inlineVideo: ({ value }) => {
        return (
          <MediaWrapper>
            <LazyVideo video={getSanityVideoSource(value)} alt={value?.alt} />
          </MediaWrapper>
        )
      },
      blockQuote: ({ value }) => (
        <Blockquote
          citeName={value?.citeName}
          citeRole={value?.citeRole}
          citeLink={value?.citeLink}
          size={value?.wide ? "large" : "small"}
        >
          {value?.text}
        </Blockquote>
      ),
      accordion: ({ value }) => (
        <Accordion title={value?.title} fillContainer="true">
          {value?.content?.map(item => {
            return (
              <AccordionItem title={item?.title}>
                <PortableText value={item?.content} />
              </AccordionItem>
            )
          })}
        </Accordion>
      ),
      table: ({ value }) => (
        <TableWrapper>
          <table>
            {value?.header && (
              <thead>
                <tr>
                  {value.header.map(header => (
                    <th>{header}</th>
                  ))}
                </tr>
              </thead>
            )}

            <tbody>
              {value?.rows?.map(row => (
                <tr>
                  {row.cells?.map(cell => (
                    <td>
                      <PortableText value={cell?.content} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrapper>
      ),
      inlineCta: ({ value }) => {
        if (value?.type === "ui") {
          return (
            <InlineCTAUI
              text={value?.text}
              link={value?.link}
              linkText={value?.linkText}
            />
          )
        }

        if (value?.type === "graphic") {
          return (
            <InlineCTAGraphic
              text={value?.text}
              link={value?.link}
              linkText={value?.linkText}
            />
          )
        }

        return (
          <InlineCTA
            text={value?.text}
            link={value?.link}
            linkText={value?.linkText}
          />
        )
      },
      youtube: ({ value }) => {
        // Extract youtube video id from url, and pass to iframe embed
        const videoId = value?.url?.split("v=")[1]

        return (
          <MediaWrapper>
            <iframe
              width="100%"
              height="315"
              src={`https://www.youtube.com/embed/${videoId}`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </MediaWrapper>
        )
      },
      feedbackMessage: ({ value }) => (
        <FeedbackMessage type={value?.type} icon={value?.icon}>
          <PortableText value={value?.content} />
        </FeedbackMessage>
      ),
      wistiaVideo: ({ value }) => (
        <WistiaVideoEmbed
          className="mb-8"
          videoId={value?.id}
          posterImage={`https://fast.wistia.com/embed/medias/${value?.id}/swatch`}
        />
      ),
      postQuote: ({ value }) => {
        return (
          <Postquote>
            <MediaWrapper>
              <SanityImage
                {...value?.authorPicture}
                alt=""
                role="presentation"
              />
            </MediaWrapper>

            <div>{value?.quote}</div>
          </Postquote>
        )
      },
      ...typeOverrides,
    },
    // Use the <Link> component for all anchor tags that point to internal links
    // so that we get localised links and gatsby clientside navigation without blogpost
    // authors needing to use a component.
    marks: {
      link: ({ children, value }) => {
        if (
          isExternalLink(convertCapsuleLink(value.href)) ||
          value?.href?.startsWith("#")
        ) {
          return <a {...value}>{children}</a>
        }

        return (
          <Link {...value} to={convertCapsuleLink(value?.href)} href={null}>
            {children}
          </Link>
        )
      },
      ...markOverrides,
    },
    block: {
      h2: props => {
        return (
          <LinkableHeading level={2} {...props}>
            {props?.children}
          </LinkableHeading>
        )
      },
      h3: props => {
        return (
          <LinkableHeading level={3} {...props}>
            {props?.children}
          </LinkableHeading>
        )
      },
    },
  }

  return (
    <PortableText
      value={children}
      components={{
        ...components,
        ...componentsThatAllPagesCanUseWithoutImporting,
      }}
    />
  )
}

export default SanityContentWrapper
